import { ButtonGradientPrimary, Colors, Icon } from '@amelio/ui-library';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { PILLAR_TILE } from './objectiveStyles';

import { useNavigate } from '@/hooks/utils/useNavigate';
import { RouteName } from '@/navigation/types';

/** Four hues of the pillar colour picker (`COLOR_PALETTE`): one per tile the row shows at its widest. */
const GHOST_TILE_COLORS = ['#0DD3C3', '#9B6CFC', '#667eea', '#FF9A8B'];

/** A pillar's colour washed out to a tint, so the tile reads as a place to fill, not as a pillar. */
const ghostTint = (color: string) =>
    `linear-gradient(135deg, color-mix(in srgb, ${color} 24%, transparent), color-mix(in srgb, ${color} 7%, transparent))`;

/** The outline of a pillar tile, built on the card's own boxes so it holds the row's exact height. */
function GhostPillarTile({ color }: { color: string }) {
    return (
        <div
            className={`${PILLAR_TILE.surface} border-2 border-dashed border-white`}
            style={{ background: ghostTint(color) }}
        >
            <div className={PILLAR_TILE.header}>
                <div className={`${PILLAR_TILE.iconBox} bg-white/55`} />
            </div>

            <div className={'flex flex-col gap-3'}>
                <div className={'h-3 w-3/5 rounded-full bg-white/70'} />
                <div className={'h-2 w-11/12 rounded-full bg-white/50'} />
                <div className={'h-2 w-2/3 rounded-full bg-white/50'} />
            </div>

            <div className={PILLAR_TILE.progressAnchor}>
                <div className={`${PILLAR_TILE.progressTrack} bg-white/50`} />
            </div>
        </div>
    );
}

/**
 * The pillar row of a company that has no pillar yet, for whoever may define them: tiles in the
 * pillar colours hold the row's place, and a card over them says what pillars are for and leads to
 * Vision and values. Tiles and card share one grid cell, so the row is as tall as the taller of the
 * two and the card never spills onto the toolbar on a narrow screen.
 */
export default function PillarsEmptyState() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const titleId = useId();

    return (
        <section
            aria-labelledby={titleId}
            className={'grid'}
        >
            <div
                aria-hidden
                className={'obj-pillar-grid col-start-1 row-start-1 flex overflow-hidden'}
            >
                {GHOST_TILE_COLORS.map((color) => (
                    <div
                        key={color}
                        className={'obj-pillar-tile flex-shrink-0'}
                    >
                        <GhostPillarTile color={color} />
                    </div>
                ))}
            </div>

            <div className={'col-start-1 row-start-1 flex items-center justify-center p-3'}>
                <div
                    className={
                        'flex w-full max-w-xl flex-col items-center gap-2.5 rounded-2xl border border-white bg-white/85 px-6 py-7 text-center shadow-lg backdrop-blur-md'
                    }
                >
                    <div
                        className={
                            'from-obj-turquoise to-obj-turquoise-deep flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br'
                        }
                    >
                        <Icon
                            name={'building-courthouse'}
                            size={'lg'}
                            color={Colors.WHITE}
                        />
                    </div>

                    <h2
                        id={titleId}
                        className={'text-navy mt-1 text-xl font-medium'}
                    >
                        {t('objectives.pillars.emptyState.title')}
                    </h2>

                    <p className={'text-obj-slate max-w-md text-sm'}>
                        {t('objectives.pillars.emptyState.description')}
                    </p>

                    <ButtonGradientPrimary
                        onClick={() => navigate({ path: RouteName.VisionValues })}
                        size={'md'}
                        fitContent
                        wrap
                        className={'mt-1'}
                        dataTestId={'objectives-pillars-define-button'}
                    >
                        {t('objectives.pillars.defineInVisionValues')}
                    </ButtonGradientPrimary>
                </div>
            </div>
        </section>
    );
}
